import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Calendar, MapPin, Clock, Receipt } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const confirmPayment = async () => {
      const sessionId = searchParams.get('session_id');
      const bookingId = searchParams.get('booking_id');

      if (!sessionId || !bookingId) {
        setError('Parametri mancanti');
        setConfirming(false);
        return;
      }

      try {
        const response = await api.post('/payments/confirm-payment', {
          session_id: sessionId,
          booking_id: bookingId
        });

        // Fetch booking details
        const bookingRes = await api.get(`/bookings/track/${response.data.booking_code}?email=`);
        setBooking(bookingRes.data);
        toast.success('Pagamento confermato!');
      } catch (err) {
        console.error('Payment confirmation error:', err);
        setError(err.response?.data?.detail || 'Errore nella conferma del pagamento');
      } finally {
        setConfirming(false);
      }
    };

    confirmPayment();
  }, [searchParams]);

  if (confirming) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center">
        <Loader2 size={48} className="animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Conferma pagamento in corso...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">!</span>
          </div>
          <h1 className="font-heading text-3xl mb-4">Errore</h1>
          <p className="text-muted-foreground mb-8">{error}</p>
          <Button onClick={() => navigate('/prenota')}>Riprova</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl mb-4">Pagamento Confermato!</h1>
          <p className="text-muted-foreground text-lg">
            La tua caparra è stata ricevuta. La prenotazione è ora confermata.
          </p>
        </motion.div>

        {booking && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <Receipt size={24} />
              <h2 className="font-heading text-2xl">Riepilogo Prenotazione</h2>
            </div>

            <dl className="space-y-4">
              <div className="flex justify-between py-3 border-b border-border">
                <dt className="text-muted-foreground">Codice Prenotazione</dt>
                <dd className="font-mono font-bold text-lg">{booking.booking_code}</dd>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <dt className="text-muted-foreground">Servizio</dt>
                <dd className="font-medium">{booking.service_type}</dd>
              </div>
              <div className="flex justify-between py-3 border-b border-border">
                <dt className="text-muted-foreground flex items-center gap-2">
                  <Calendar size={16} /> Data
                </dt>
                <dd className="font-medium">{booking.preferred_date}</dd>
              </div>
              {booking.time_slot && (
                <div className="flex justify-between py-3 border-b border-border">
                  <dt className="text-muted-foreground flex items-center gap-2">
                    <Clock size={16} /> Orario
                  </dt>
                  <dd className="font-medium">{booking.time_slot}</dd>
                </div>
              )}
              <div className="flex justify-between py-3 border-b border-border">
                <dt className="text-muted-foreground flex items-center gap-2">
                  <MapPin size={16} /> Luogo
                </dt>
                <dd className="font-medium">{booking.location}</dd>
              </div>
              {booking.deposit_amount && (
                <div className="flex justify-between py-3 bg-green-50 px-3 rounded">
                  <dt className="font-medium text-green-700">Caparra Pagata</dt>
                  <dd className="font-bold text-green-700">€{booking.deposit_amount.toFixed(2)}</dd>
                </div>
              )}
            </dl>

            <div className="mt-8 p-4 bg-muted/50 rounded text-sm text-muted-foreground">
              <p>Conserva il codice prenotazione <strong>{booking.booking_code}</strong> per gestire la tua prenotazione.</p>
            </div>
          </motion.div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
          <Link to="/gestisci-prenotazione">
            <Button variant="outline" data-testid="manage-booking-link">Gestisci Prenotazione</Button>
          </Link>
          <Link to="/">
            <Button>Torna alla Home</Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
